import {
  IndexTable,
  Layout,
  LegacyCard,
  EmptySearchResult,
  Banner,
  Badge,
} from "@shopify/polaris";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { HistoricalSkeletonTable } from "../../../components/skeletonLoader";
import { paypalSettingApi } from "../../../redux/troubleshooting/paypalSetting/Action";
import {
  displayBoolean,
  displayCount,
  displayDateTime,
  displayText,
  ReadOnlyTablePagination,
  SUPPORT_TABLE_PAGE_SIZE,
} from "./readOnlyTable";

const PaypalSettings = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeShopId = String(id ?? "");
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({
    shopId: routeShopId,
    pageNumber: 0,
  });
  const pageNumber =
    pagination.shopId === routeShopId ? pagination.pageNumber : 0;
  const paypalSettingData = useSelector(
    (store) => store.requriedPaypalSettingData
  );
  const isCurrentStore = paypalSettingData.shopId === String(id);
  const paypalData =
    isCurrentStore &&
    paypalSettingData.isSuccess &&
    Array.isArray(paypalSettingData.data?.content)
      ? paypalSettingData.data.content
      : [];
  const readError =
    isCurrentStore && paypalSettingData.isError
      ? paypalSettingData.error
      : null;
  const isLoading = !isCurrentStore || paypalSettingData.isRequest;

  useEffect(() => {
    dispatch(
      paypalSettingApi({
        storeId: id,
        pageNumber,
        pageSize: SUPPORT_TABLE_PAGE_SIZE,
      })
    );
  }, [dispatch, id, pageNumber]);

  useEffect(() => {
    if (
      isCurrentStore &&
      (paypalSettingData?.data?.message === "Actual error message" ||
        paypalSettingData.error === "Actual error message")
    ) {
      navigate("/logout");
    }
  }, [isCurrentStore, navigate, paypalSettingData.data, paypalSettingData.error]);

  const resourceName = {
    singular: "setting",
    plural: "settings",
  };

  const emptyStateMarkup = (
    <EmptySearchResult
      title="No PayPal settings found"
      description="No PayPal account settings were returned for this page."
      withIllustration
    />
  );

  const rowMarkup = paypalData?.map(
    (
      {
        id,
        merchantAccountId,
        description,
        totalOrders,
        payerId,
        payerEmail,
        status,
        defaultAccount,
        tokenRefreshed,
        rateLimitReached,
        rateLimitReachedAt,
        creationDate,
      },
      index
    ) => {
      const normalizedMerchantAccountId =
        typeof merchantAccountId === "string" && merchantAccountId.trim()
          ? merchantAccountId.trim()
          : null;
      const accountId = displayText(normalizedMerchantAccountId ?? payerId);
      const distinctPayerId =
        payerId && String(payerId) !== String(normalizedMerchantAccountId)
          ? String(payerId)
          : null;

      return (
        <IndexTable.Row
          id={String(id ?? `${routeShopId}-paypal-${index}`)}
          key={String(id ?? `${routeShopId}-paypal-${index}`)}
          position={index}
        >
          <IndexTable.Cell>
            <p>{accountId}</p>
            {distinctPayerId ? (
              <p className="secondaryText">Payer ID: {distinctPayerId}</p>
            ) : null}
            <p className="secondaryText">
              Email: {displayText(payerEmail)}
            </p>
          </IndexTable.Cell>
          <IndexTable.Cell>
            {displayText(description, "Not recorded")}
          </IndexTable.Cell>
          <IndexTable.Cell>{displayCount(totalOrders)}</IndexTable.Cell>
          <IndexTable.Cell>
            <Badge
              status={
                status === true
                  ? "success"
                  : status === false
                  ? "critical"
                  : "new"
              }
            >
              {displayBoolean(status, "Active", "Inactive")}
            </Badge>
            <p className="secondaryText">
              {displayBoolean(defaultAccount, "Default account", "Not default")}
            </p>
          </IndexTable.Cell>
          <IndexTable.Cell>
            {displayBoolean(
              tokenRefreshed,
              "Refresh recorded",
              "No refresh recorded"
            )}
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Badge
              status={
                rateLimitReached === true
                  ? "critical"
                  : rateLimitReached === false
                  ? "success"
                  : "new"
              }
            >
              {displayBoolean(
                rateLimitReached,
                "Rate limited",
                "Not rate limited"
              )}
            </Badge>
            <p className="secondaryText">
              Recorded: {displayDateTime(rateLimitReachedAt)}
            </p>
          </IndexTable.Cell>
          <IndexTable.Cell>{displayDateTime(creationDate)}</IndexTable.Cell>
        </IndexTable.Row>
      );
    }
  );

  const accountHeading = (
    <span className="d-flex gap-10">
      <span>PayPal account</span>
    </span>
  );
  const descriptionHeading = (
    <span className="d-flex gap-10">
      <span>Description</span>
    </span>
  );
  const totalOrdersHeading = (
    <span className="d-flex gap-10">
      <span>Total Orders</span>
    </span>
  );
  const statusHeading = (
    <span className="d-flex gap-10">
      <span>Status</span>
    </span>
  );

  const creationDateHeading = (
    <span className="d-flex gap-10">
      <span>Creation Date</span>
    </span>
  );
  const handlePageChange = (nextPage) =>
    setPagination({ shopId: routeShopId, pageNumber: nextPage });

  return (
    <Layout.Section>
      <div className="wrapper">
        <LegacyCard sectioned>
          <p className="card_title" style={{ padding: "15px" }}>
            PayPal settings
          </p>
          <LegacyCard sectioned>
            {isLoading ? (
              <HistoricalSkeletonTable />
            ) : readError ? (
              <Banner
                title="PayPal settings could not be loaded"
                status="critical"
              >
                <p>{readError}</p>
              </Banner>
            ) : (
              <IndexTable
                resourceName={resourceName}
                itemCount={paypalData?.length || 0}
                emptyState={emptyStateMarkup}
                headings={[
                  { id: "paypal-account", title: accountHeading },
                  { id: "description", title: descriptionHeading },
                  { id: "total-orders", title: totalOrdersHeading },
                  { id: "status", title: statusHeading },
                  { id: "token", title: "Token history" },
                  { id: "rate-limit", title: "Rate limit" },
                  { id: "creation-date", title: creationDateHeading },
                ]}
                selectable={false}
              >
                {rowMarkup}
              </IndexTable>
            )}
            {!isLoading && !readError ? (
              <ReadOnlyTablePagination
                response={paypalSettingData.data}
                currentPage={pageNumber}
                rowCount={paypalData.length}
                noun="PayPal setting"
                onPageChange={handlePageChange}
              />
            ) : null}
          </LegacyCard>
        </LegacyCard>
      </div>
    </Layout.Section>
  );
};

export default PaypalSettings;
